/**
 * @file MilkPowderPricing.js
 * @version 1.6.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-04
 * @modified 2026-10-28
 * @description Milk Powder pricing (sales account 200P). Two tabs in one
 *              screen: weekly Cost Prices (cost per product per week) and
 *              Contract Pricing (per-bag SALE prices per customer + product,
 *              each From one day To another — DD/MM/YY — with margin vs the
 *              cost in force at its To date). Both show the last 4 records
 *              with a full-history chart modal.
 *
 *              1.6.0 (28 Oct 2026, Cam — "record which milk powder price each
 *              order used"): each customer + product shows the orders priced
 *              from (or added to) its prices — no tonnage to deduct from, so
 *              what was used — with "Add orders…" (ContractOrdersModal) and a
 *              header "Refresh Auto-links" a month at a time.
 */

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDate, formatDateDDMMYY } from '../utils/dateFormatters';
import { formatRange, formatRangeCompact, rangesOverlap, toDay, coversDay } from '../utils/milkPowderDates';
import DateInput from './DateInput';
import './SearchLayout.css';
import './ContractPricing.css';
import './MilkPowderPricing.css';
import { ScopeLinkBadge } from './PricingBadges';
import Input from './Input';
import Dropdown from './Dropdown';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import ContactModal from './ContactModal';
import MilkPowderCostModal from './MilkPowderCostModal';
import MilkPowderBulkCostModal from './MilkPowderBulkCostModal';
import MilkPowderCostHistoryModal from './MilkPowderCostHistoryModal';
import MilkPowderContractPriceModal from './MilkPowderContractPriceModal';
import MilkPowderContractHistoryModal from './MilkPowderContractHistoryModal';
import { useSlideOutButton } from './SlideOutButton';
import RefreshAutoLinksButton from './RefreshAutoLinksButton';
import ContractOrdersModal from './ContractOrdersModal';
import AlertDialog from './AlertDialog';
import { milkMonthPeriods, usageWords } from '../utils/contractOrders';
import { formatTonnes } from '../utils/numberFormatters';
import usePersistedState from '../hooks/usePersistedState';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';

// One cost cell in the Cost Prices table: week-commencing date badge above the
// cost figure (with cost-per-bag in brackets). The flex wrapper centres the
// content without changing the row height.
function CostCell({ cell, bagsPerTonne, isCurrent }) {
  if (!cell) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const cost = parseFloat(cell.cost);
  const perBag = bagsPerTonne ? cost / parseFloat(bagsPerTonne) : null;
  return (
    <div className="mp-cost-cell">
      <span className={isCurrent ? 'mp-week-badge current' : 'mp-week-badge'}>{formatDate(cell.week_commencing)}</span>
      <span className="price">
        {formatCurrency(cost)}
        {perBag != null && <span className="mp-per-bag"> ({formatCurrency(perBag)})</span>}
      </span>
    </div>
  );
}

// One price cell in the Contract Pricing table: a compact From – To badge
// (DD/MM–DD/MM/YY; the full dates on hover) above the per-bag sale price,
// with margin (sale − cost at its To date) below. The badge is highlighted
// only on the price covering today (London). Flex wrapper keeps the row
// height unchanged.
function ContractPriceCell({ cell, today }) {
  if (!cell) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const price = parseFloat(cell.price);
  const cost = cell.cost_per_bag != null ? parseFloat(cell.cost_per_bag) : null;
  const margin = cost != null ? price - cost : null;
  const row = { valid_from: cell.valid_from, valid_to: cell.valid_to, price_month: cell.month };
  const current = coversDay(row, today);
  return (
    <div className="mp-cost-cell">
      <span className={current ? 'mp-week-badge current' : 'mp-week-badge'}
        title={`${formatRange(row)}${current ? ' — in force today' : ''}`}>
        {formatRangeCompact(row)}
      </span>
      <span className="price">{formatCurrency(price)}</span>
      {margin != null && (
        <span className={`mp-margin ${margin >= 0 ? 'pos' : 'neg'}`}>
          {margin >= 0 ? '+' : ''}{formatCurrency(margin)}
        </span>
      )}
    </div>
  );
}

// The tab strip lives in the shared content-tab-strip above the page panel
// (rendered by Dashboard, which also adds `tabbed` to .content-inner). This
// component just renders the active tab's content. `activeTab` defaults to
// 'costs' so the component still works if rendered without the strip.
function MilkPowderPricing({ activeTab = 'costs' }) {
  return (
    <div className="contract-pricing milk-powder-pricing">
      {activeTab === 'costs' ? <CostPricesTab /> : <ContractsTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — weekly Cost Prices. One row per milk powder product showing the
// latest weekly cost; edit sets a week's cost, history shows the timeline.
// ---------------------------------------------------------------------------
/*
 * What each tab's Clear Filters resets to — and so what "no filters applied"
 * means for the button's highlight (utils/activeFilters.js).
 */
const COST_CLEARED_FILTERS = { search: '', limit: '50', activeOnly: true };
const CONTRACT_CLEARED_FILTERS = { search: '', rep: '', pricedOn: '', limit: '50', activeOnly: true };
const FILTER_LABELS = { activeOnly: 'Archived', rep: 'Rep', pricedOn: 'Priced on' };

function CostPricesTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [costModalProduct, setCostModalProduct] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // 7 cols: product, -3, -2, -1, current, notes, actions.
  const [sorting, setSorting] = usePersistedState('milkPowder.cost.sorting', { field: '', direction: 'asc' });
  const [filters, setFilters] = usePersistedState('milkPowder.cost.filters', {
    search: '', limit: '50', activeOnly: true
  });
  const [pagination, setPagination] = usePersistedState('milkPowder.cost.pagination', {
    page: 1, limit: 50, total: 0, totalPages: 0
  });

  useSlideOutButton([]);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing/milk-powder/cost-prices`, { credentials: 'include' });
      const data = await res.json();
      setProducts(data.data || []);
    } catch (e) {
      console.error('Error loading milk powder cost prices:', e);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));
  const handleSort = (field) => setSorting(prev => ({
    field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
  }));
  const handleClearFilters = () => {
    setFilters(prev => ({ ...COST_CLEARED_FILTERS, limit: prev.limit || '50' }));
    setSorting({ field: '', direction: 'asc' });
  };

  // Any header filter on lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, COST_CLEARED_FILTERS, { labels: FILTER_LABELS });
  const filtersApplied = headerFilters.count > 0;

  const filtered = products
    .filter(p => {
      if (filters.activeOnly && p.is_archived) return false;
      if (!filters.search) return true;
      return matchesAllTerms([p.item_name, p.item_code], filters.search);
    })
    .sort((a, b) => {
      if (!sorting.field) return 0;
      let aVal = a[sorting.field];
      let bVal = b[sorting.field];
      if (sorting.field === 'current_cost') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });

  useEffect(() => {
    const limit = parseInt(filters.limit) || 50;
    setPagination(prev => ({
      ...prev, page: 1, limit, total: filtered.length, totalPages: Math.ceil(filtered.length / limit)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, filters.search, filters.activeOnly, filters.limit, sorting.field, sorting.direction]);

  const paginated = filtered.slice((pagination.page - 1) * pagination.limit, pagination.page * pagination.limit);
  const changePage = (p) => {
    if (p >= 1 && p <= pagination.totalPages) setPagination(prev => ({ ...prev, page: p }));
  };

  return (
    <>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Manage weekly cost prices for milk powder products</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>The latest week is the current cost — view history for the full timeline</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button
                className="btn-slideout"
                onClick={() => setBulkOpen(true)}
                disabled={products.filter(p => !p.is_archived).length === 0}
                title="Set this week's cost for every active milk powder product at once"
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">edit_note</span></div>
                <span className="btn-label">Edit All Costs</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters" style={{ paddingBottom: 'var(--spacing-lg)' }}>
        <div className="filter-row weekly">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['product', 'item code']} />
            </label>
            <Input type="text" placeholder="Product name or code..." value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)} icon="search" />
          </div>
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown value={filters.limit} onChange={(v) => handleFilterChange('limit', v)}
              options={[{ value: '20', label: '20' }, { value: '50', label: '50' }, { value: '100', label: '100' }, { value: '200', label: '200' }]}
              placeholder="50" />
          </div>
          <div className="filter-group">
            <label>Active Only</label>
            <div className="checkbox-container">
              <div className="check light">
                <input id="mp-cost-active-only" type="checkbox" checked={!!filters.activeOnly}
                  onChange={(e) => handleFilterChange('activeOnly', e.target.checked)} />
                <label htmlFor="mp-cost-active-only"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="table-toolbar" style={{ gridTemplateColumns: "1fr" }}>
        {pagination.totalPages > 0 && (
          <PaginationControls pagination={pagination} onPageChange={changePage} />
        )}
      </div>

      <div className="table-container">
        {loading ? <LoadingSpinner /> : (
          <table className="data-table weekly">
            <thead>
              <tr>
                <th style={{ textAlign: "center !important", width: "85px" }}>Code</th>
                <th className="sortable" style={{ textAlign: "left" }} onClick={() => handleSort('item_name')}>
                  Product {sorting.field === 'item_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th style={{ textAlign: 'center', width: "135px" }}>-3</th>
                <th style={{ textAlign: 'center', width: "135px" }}>-2</th>
                <th style={{ textAlign: 'center', width: "135px" }}>-1</th>
                <th style={{ textAlign: 'center', width: "135px" }} className="sortable" onClick={() => handleSort('current_cost')}>
                  Current {sorting.field === 'current_cost' && (sorting.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th style={{ minWidth: "155px" }}>Notes</th>
                <th style={{ width: "120px", minWidth: "120px", maxWidth: "120px" }} className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan="7" className="text-center">No milk powder products found</td></tr>
              ) : (
                paginated.map((p) => {
                  // recent = up to 4 weekly costs, chronological asc. Right-align
                  // into [-3, -2, -1, current] so the newest sits in the last slot.
                  const recent = Array.isArray(p.recent) ? p.recent : [];
                  const cells = [null, null, null, null];
                  recent.forEach((c, i) => { cells[4 - recent.length + i] = c; });
                  return (
                  <tr key={p.inventory_item_id} className={p.is_archived ? 'archived' : ''}
                    style={{ cursor: 'pointer' }} onClick={() => setCostModalProduct(p)}>
                    <td style={{ textAlign: "center" }}>
                      {p.item_code && <div className="product-code">{p.item_code}</div>}
                    </td>
                    <td>
                      <div className="mp-product-cell">
                        <div>{p.item_name}</div>
                        {p.is_archived && <span className="archived-badge">ARCHIVED</span>}
                      </div>
                    </td>
                    {cells.map((cell, idx) => (
                      <td key={idx} style={{ textAlign: 'center' }}>
                        <CostCell cell={cell} bagsPerTonne={p.bags_per_tonne} isCurrent={idx === 3} />
                      </td>
                    ))}
                    <td style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>{p.notes || ''}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="actions-col">
                        <button className="btn-icon" onClick={() => setCostModalProduct(p)} title="Set this week's cost">
                          <span className="material-symbols-rounded">edit</span>
                        </button>
                        <button className="btn-icon" onClick={() => setHistoryProduct(p)}
                          title={parseInt(p.history_count) > 0 ? 'View price history' : 'No history yet'}
                          disabled={parseInt(p.history_count) === 0}>
                          <span className="material-symbols-rounded">history</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {costModalProduct && (
        <MilkPowderCostModal
          product={costModalProduct}
          onClose={() => setCostModalProduct(null)}
          onSave={() => { setCostModalProduct(null); loadProducts(); }}
        />
      )}

      {bulkOpen && (
        <MilkPowderBulkCostModal
          products={products.filter(p => !p.is_archived)}
          onClose={() => setBulkOpen(false)}
          onSave={() => { setBulkOpen(false); loadProducts(); }}
        />
      )}

      {historyProduct && (
        <MilkPowderCostHistoryModal
          product={historyProduct}
          onClose={() => setHistoryProduct(null)}
          onChanged={loadProducts}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Contract Pricing. Per-bag SALE prices per customer + product
// (pricing.milk_powder_contract_prices), each From one day To another. Shows
// the last 4 recorded prices with margin vs the cost in force at each To date,
// and the latest price's From / To as their own sortable columns; "Priced on"
// keeps the pairs with a price covering that day. The history action opens
// the full timeline + chart for that customer + product.
// ---------------------------------------------------------------------------
function ContractsTab() {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);   // { pair, price?, mode } to prefill, or { mode } for blank
  const [historyTarget, setHistoryTarget] = useState(null);
  const [selectedContactId, setSelectedContactId] = usePersistedState('milkPowder.contract.contactModalId', null);
  // The orders counted against a customer + product's prices (28 Oct 2026).
  const [ordersFor, setOrdersFor] = useState(null);
  const [alertDialog, setAlertDialog] = useState(null);
  const monthPeriods = useMemo(() => milkMonthPeriods(), []);
  const openOrders = (p) => setOrdersFor({
    set: { kind: 'milk_powder', ids: p.price_ids || [] },
    title: `${p.company_name} — ${p.item_name || p.item_code || 'milk powder'}`
  });

  // Default: newest "Latest" month at the top. New persisted key so the new
  // default applies even where the old sort was already remembered.
  const [sorting, setSorting] = usePersistedState('milkPowder.contract.sortLatest', { field: 'latest', direction: 'desc' });
  const [filters, setFilters] = usePersistedState('milkPowder.contract.filters', { ...CONTRACT_CLEARED_FILTERS });
  const [pagination, setPagination] = usePersistedState('milkPowder.contract.pagination', { page: 1, limit: 50, total: 0, totalPages: 0 });

  useSlideOutButton([]);

  useEffect(() => {
    loadPairs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPairs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing/milk-powder/contract-prices`, { credentials: 'include' });
      const data = await res.json();
      setPairs(data.data || []);
    } catch (e) {
      console.error('Error loading contract prices:', e);
      setPairs([]);
    } finally {
      setLoading(false);
    }
  };

  const reps = useMemo(() => [...new Set(pairs.map(p => p.sales_rep).filter(Boolean))].sort(), [pairs]);
  // The latest price's From / To ('YYYY-MM-DD', so they sort as text).
  const latestFrom = p => toDay(p.latest_from) || '';
  const latestTo = p => toDay(p.latest_to) || '';

  const handleFilterChange = (f, v) => setFilters(prev => ({ ...prev, [f]: v }));
  const handleSort = (field) => setSorting(prev => ({ field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc' }));
  const handleClearFilters = () => { setFilters(prev => ({ ...CONTRACT_CLEARED_FILTERS, limit: prev.limit || '50' })); setSorting({ field: '', direction: 'asc' }); };

  // Any header filter on lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CONTRACT_CLEARED_FILTERS, { labels: FILTER_LABELS });
  const filtersApplied = headerFilters.count > 0;

  const handleArchive = async (p) => {
    const archiving = !p.is_archived;
    try {
      await fetch(`${API_URL}/api/pricing/milk-powder/contract-prices/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ account_id: p.account_id, inventory_item_id: p.inventory_item_id, is_archived: archiving })
      });
      loadPairs();
    } catch (e) { console.error('Error archiving contract pricing:', e); }
  };

  const filtered = useMemo(() => (pairs || [])
    .filter(p => {
      if (filters.activeOnly && p.is_archived) return false;
      if (filters.rep && p.sales_rep !== filters.rep) return false;
      if (filters.pricedOn) {
        const day = { from: filters.pricedOn, to: filters.pricedOn };
        if (!(p.ranges || []).some(r => rangesOverlap(day, { from: toDay(r.from), to: toDay(r.to) }))) return false;
      }
      if (!filters.search) return true;
      return matchesAllTerms([p.company_name, p.account_no, p.item_name, p.item_code], filters.search);
    })
    .sort((a, b) => {
      if (!sorting.field) return 0;
      let av, bv;
      if (sorting.field === 'latest') { av = latestFrom(a); bv = latestFrom(b); }
      else if (sorting.field === 'latest_to') { av = latestTo(a); bv = latestTo(b); }
      else { av = (a[sorting.field] || '').toString().toLowerCase(); bv = (b[sorting.field] || '').toString().toLowerCase(); }
      if (av < bv) return sorting.direction === 'asc' ? -1 : 1;
      if (av > bv) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    }), [pairs, filters, sorting]);

  useEffect(() => {
    const limit = parseInt(filters.limit) || 50;
    setPagination(prev => ({ ...prev, page: 1, limit, total: filtered.length, totalPages: Math.ceil(filtered.length / limit) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, filters.search, filters.rep, filters.pricedOn, filters.activeOnly, filters.limit, sorting.field, sorting.direction]);

  // Today in London — the day a price must cover to be highlighted.
  const today = toDay(new Date());

  const paginated = filtered.slice((pagination.page - 1) * pagination.limit, pagination.page * pagination.limit);
  const changePage = (p) => { if (p >= 1 && p <= pagination.totalPages) setPagination(prev => ({ ...prev, page: p })); };

  return (
    <>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Manage milk powder customer contract pricing (per bag)</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>Sale prices From – To with margin vs the cost in force at the To date — view history for the full timeline</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <RefreshAutoLinksButton
              periods={monthPeriods}
              permissions={['milk_powder.edit', 'contract_pricing.edit']}
              needsWords="Needs 'Edit milk powder pricing'"
              intro="Links each order delivering in the month to the milk powder price the order screen would price it from (milk powder has no contract periods, so one month at a time)."
              onDone={loadPairs}
              onAlert={setAlertDialog}
            />
            <div>
              <button className="btn-slideout" onClick={() => setEditTarget({ mode: 'edit' })}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                <span className="btn-label">Add New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters" style={{ paddingBottom: 'var(--spacing-lg)' }}>
        <div className="filter-row contracts">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['customer', 'account no.', 'product', 'item code']} />
            </label>
            <Input type="text" placeholder="Customer, account #, product..." value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)} icon="search" />
          </div>
          <div className="filter-group">
            <label>Rep</label>
            <Dropdown value={filters.rep} onChange={(v) => handleFilterChange('rep', v)}
              options={[{ value: '', label: 'All Reps' }, ...reps.map(r => ({ value: r, label: r }))]} />
          </div>
          <div className="filter-group">
            <label title="Only customers + products with a price covering this day">Priced on</label>
            <DateInput shortYear value={filters.pricedOn || ''} onChange={(e) => handleFilterChange('pricedOn', e.target.value)} placeholderText="Any day" isClearable />
          </div>
          <div className="filter-group">
            <label>Active Only</label>
            <div className="checkbox-container">
              <div className="check light">
                <input id="mp-contract-active-only" type="checkbox" checked={!!filters.activeOnly}
                  onChange={(e) => handleFilterChange('activeOnly', e.target.checked)} />
                <label htmlFor="mp-contract-active-only"></label>
              </div>
            </div>
          </div>
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown value={filters.limit} onChange={(v) => handleFilterChange('limit', v)}
              options={[{ value: '20', label: '20' }, { value: '50', label: '50' }, { value: '100', label: '100' }, { value: '200', label: '200' }]} placeholder="50" />
          </div>
        </div>
      </div>

      <div className="table-toolbar">
        {pagination.totalPages > 0 && (<PaginationControls pagination={pagination} onPageChange={changePage} />)}
      </div>

      <div className="table-container">
        {loading ? <LoadingSpinner /> : (
          <table className="data-table weekly">
            <thead>
              <tr>
                <th className="sortable" style={{ textAlign: 'left' }} onClick={() => handleSort('company_name')}>
                  Customer {sorting.field === 'company_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable" style={{ textAlign: 'left' }} onClick={() => handleSort('item_name')}>
                  Product {sorting.field === 'item_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th style={{ textAlign: 'center' }}>-3</th>
                <th style={{ textAlign: 'center' }}>-2</th>
                <th style={{ textAlign: 'center' }}>-1</th>
                <th style={{ textAlign: 'center' }}>Latest</th>
                <th className="sortable mp-date-col" style={{ textAlign: 'left' }} onClick={() => handleSort('latest')} title="The latest price's From date">
                  From {sorting.field === 'latest' && (sorting.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable mp-date-col" style={{ textAlign: 'left' }} onClick={() => handleSort('latest_to')} title="The latest price's To date">
                  To {sorting.field === 'latest_to' && (sorting.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable" style={{ textAlign: 'left' }} onClick={() => handleSort('sales_rep')}>
                  Rep {sorting.field === 'sales_rep' && (sorting.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th>Notes</th>
                <th title="Orders priced from (or added to) these prices — what was used against them">Orders</th>
                <th style={{ width: '200px' }} className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan="12" className="text-center">No contract prices found</td></tr>
              ) : (
                paginated.map((p) => {
                  const recent = Array.isArray(p.recent) ? p.recent : [];
                  const cells = [null, null, null, null];
                  recent.forEach((c, i) => { cells[4 - recent.length + i] = c; });
                  return (
                    <tr key={`${p.account_id}-${p.inventory_item_id}`} className={p.is_archived ? 'archived' : ''} style={{ cursor: 'pointer' }} onClick={() => setEditTarget({ pair: p, mode: 'view' })}>
                      <td>
                        <span className="name-with-badge">
                          {p.account_no && (
                            <span className="account-badge-inline" style={p.account_id ? { cursor: 'pointer' } : undefined}
                              onClick={p.account_id ? (e) => { e.stopPropagation(); setSelectedContactId(p.account_id); } : undefined}>{p.account_no}</span>
                          )}
                          <span className="contact-link" onClick={(e) => { e.stopPropagation(); if (p.account_id) setSelectedContactId(p.account_id); }}>{p.company_name}</span>
                          {p.is_archived && <span className="archived-badge">ARCHIVED</span>}
                        </span>
                        <span className="address-with-space">
                          <span className="scope-link-slot"><ScopeLinkBadge scopeKind={p.scope_kind} /></span>
                          {p.address_line && <span className="delivery-address">{p.address_line}</span>}
                        </span>
                      </td>
                      <td>
                        <div className="mp-product-cell">
                          {p.item_code && <div className="product-code">{p.item_code}</div>}
                          <div>{p.item_name}</div>
                        </div>
                      </td>
                      {cells.map((cell, idx) => (
                        <td key={idx} style={{ textAlign: 'center' }}>
                          <ContractPriceCell cell={cell} today={today} />
                        </td>
                      ))}
                      <td className="mp-date-col">{p.latest_from ? formatDateDDMMYY(toDay(p.latest_from)) : '—'}</td>
                      <td className="mp-date-col">{p.latest_to ? formatDateDDMMYY(toDay(p.latest_to)) : '—'}</td>
                      <td style={{ fontSize: 'var(--font-sm)' }}>{p.sales_rep || '—'}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>{p.notes || ''}</td>
                      <td style={{ fontSize: 'var(--font-sm)' }} onClick={(e) => { e.stopPropagation(); openOrders(p); }}
                        title="Open the orders counted against these prices">
                        {p.orders_used
                          ? (p.orders_used.lines ? usageWords(p.orders_used, formatTonnes) : <span style={{ color: 'var(--text-muted)' }}>None yet</span>)
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="actions-col">
                          <button className="btn-icon" onClick={() => openOrders(p)}
                            title="Add orders… — the orders priced from these prices; add any of this customer's orders by hand, or take one off">
                            <span className="material-symbols-rounded">playlist_add</span>
                          </button>
                          <button className="btn-icon" onClick={() => setEditTarget({ pair: p, mode: 'edit' })} title="Add a price">
                            <span className="material-symbols-rounded">add</span>
                          </button>
                          <button className={`btn-icon${p.latest_id ? '' : ' btn-disabled'}`} disabled={!p.latest_id}
                            onClick={() => setEditTarget({
                              pair: p,
                              price: { id: p.latest_id, valid_from: p.latest_from, valid_to: p.latest_to, sale_price: p.latest_price, notes: p.notes },
                              mode: 'edit'
                            })}
                            title={p.latest_id ? `Edit the latest price (${formatRange({ valid_from: p.latest_from, valid_to: p.latest_to })})` : 'No price to edit'}>
                            <span className="material-symbols-rounded">edit</span>
                          </button>
                          <button className="btn-icon" onClick={() => setHistoryTarget(p)}
                            title={parseInt(p.history_count) > 0 ? 'View price history' : 'No history yet'}
                            disabled={parseInt(p.history_count) === 0}>
                            <span className="material-symbols-rounded">history</span>
                          </button>
                          <button className="btn-icon" onClick={() => handleArchive(p)} title={p.is_archived ? 'Unarchive' : 'Archive'}>
                            <span className="material-symbols-rounded">{p.is_archived ? 'unarchive' : 'archive'}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {editTarget && (
        <MilkPowderContractPriceModal
          pair={editTarget.pair || null}
          price={editTarget.price || null}
          mode={editTarget.mode || 'edit'}
          onClose={() => { setEditTarget(null); loadPairs(); }}
          onSave={() => { setEditTarget(null); loadPairs(); }}
        />
      )}

      {historyTarget && (
        <MilkPowderContractHistoryModal
          pair={historyTarget}
          onClose={() => { setHistoryTarget(null); loadPairs(); }}
          onChanged={loadPairs}
          onEdit={(price) => { const pair = historyTarget; setHistoryTarget(null); setEditTarget({ pair, price, mode: 'edit' }); }}
        />
      )}

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal contactId={selectedContactId} onClose={() => setSelectedContactId(null)} />,
        document.body
      )}

      {ordersFor && (
        <ContractOrdersModal
          set={ordersFor.set}
          title={ordersFor.title}
          editPermissions={['milk_powder.edit', 'contract_pricing.edit']}
          onClose={() => { setOrdersFor(null); loadPairs(); }}
        />
      )}

      <AlertDialog
        isOpen={!!alertDialog?.isOpen}
        type={alertDialog?.type || 'info'}
        message={alertDialog?.message}
        onClose={() => setAlertDialog(null)}
      />
    </>
  );
}

export default MilkPowderPricing;
